import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBus } from "@fortawesome/free-solid-svg-icons";
import { PopoverBody } from "react-bootstrap";

import olOSMSource from "ol/source/OSM";
import olVectorSource from "ol/source/Vector";
import olClusterSource from "ol/source/Cluster";
import olPolygon from "ol/geom/Polygon";
import olPoint from "ol/geom/Point";
import olFeature, { FeatureLike as olFeatureLike } from "ol/Feature";
import { Extent as olExtent } from "ol/extent";

import { Map } from "../../components/reactOL/map";
import { Layers, Mask, Tile, Vector } from "../../components/reactOL/layers";
import {
  Controls,
  FullScreen,
  ScaleBar,
  ZoomButtons,
  ZoomSlide,
} from "../../components/reactOL/controls";
import {
  Interactions,
  MapClick,
  ZoomToCluster,
} from "../../components/reactOL/interactions";
import { Epsg27700 } from "../../components/reactOL/projections";
import ConstrainedView from "../../components/reactOL/views/ConstrainedView";
import { Overlays, PopupOverlay } from "../../components/reactOL/overlays";

import {
  BoundaryStyle,
  ClusterStyle,
  MaskFill,
  StationStyle,
} from "../../theme/mapStyles";

import { useAlertContext } from "../../components/alert/AlertContext";
import FareZoneService from "../../services/FareZoneService";
import { Scheme } from "../../types/Scheme";

const MapContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100vh;
`;

const StationButtons = styled.div`
  position: absolute;
  left: 35px;
  z-index: 1;
  padding: 7px;
  width: calc(100% - 35px);
`;

function Public() {
  const navigate = useNavigate();
  const alertContext = useAlertContext();
  const { fareZoneId, psCode } = useParams();

  const view = useMemo(
    () =>
      new ConstrainedView({
        center: [451073, 324546],
        projection: Epsg27700,
        minZoom: 5,
        maxZoom: 15,
      }),
    [],
  );

  const boundaryVectorSource = useMemo(() => new olVectorSource(), []);
  const stopVectorSource = useMemo(() => new olVectorSource(), []);
  const stationsVectorSource = useMemo(() => new olVectorSource(), []);
  const stopClusterSource = useMemo(
    () =>
      new olClusterSource({
        distance: 15,
        minDistance: 15,
        source: stopVectorSource,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const osm = useMemo(() => new olOSMSource(), []);

  const fareZoneService = new FareZoneService(alertContext.showAlert);

  const [fareZoneFeature, setFareZoneFeature] = useState<olFeature>();
  const [selectedStop, setSelectedStop] = useState<olFeature | undefined>();
  const [showStops, setShowStops] = useState(false);

  useEffect(() => {
    if (fareZoneId === undefined) {
      navigate("/");
      return;
    }
    getFareZone(fareZoneId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fareZoneId]);

  const getFareZone = (fareZoneId: string) => {
    fareZoneService.getById(fareZoneId, false).then((fareZone) => {
      if (!fareZone) {
        navigate("/");
        return;
      }

      const scheme = psCode
        ? fareZone.schemes.find((x) => x.psCode === psCode)
        : {
            stations: fareZone.schemes
              .map((x) => x.stations)
              .reduce(
                (accumulator, currentArray) => [
                  ...accumulator,
                  ...currentArray,
                ],
                [],
              ),
            exclusions: fareZone.schemes.map((s) => s.exclusions).join("\n"),
          };

      if (!scheme) {
        navigate("/");
        return;
      }

      const fareZoneWithoutSchemes = { ...fareZone, schemes: undefined };

      const feature = new olFeature({
        ...fareZoneWithoutSchemes,
        selectedScheme: scheme,
        boundary: null,
        geometry: new olPolygon(fareZone.boundary.coordinates),
      });
      boundaryVectorSource.addFeature(feature);
      setFareZoneFeature(feature);

      getStations(scheme);
      getStopsInBoundary(fareZoneId);

      const extent = feature.getGeometry()?.getExtent() as olExtent;
      view.fit(extent);
      view.ConstrainToExtent = extent;
    });
  };

  const getStations = (scheme: Scheme) => {
    const features = scheme.stations.map(
      (station) =>
        new olFeature({
          ...station,
          location: null,
          geometry: new olPoint(station.location.coordinates),
        }),
    );
    stationsVectorSource.addFeatures(features);
  };

  const getStopsInBoundary = (fareZoneId: string) => {
    fareZoneService.getStopsInFareZone(fareZoneId, false).then((response) => {
      if (!response) return;
      const features = response.features.map((feature: any) => {
        const f = new olFeature({
          geometry: new olPoint(feature.geometry.coordinates),
          id: feature.properties.Id,
          name: feature.properties.Name,
          naptanCode: feature.properties.NaptanCode,
        });
        return f;
      });
      stopVectorSource.addFeatures(features);
    });
  };

  const stopClickHandler = (features: olFeatureLike[]) => {
    let selectedStop;
    if (features.length) {
      var clusterFeatures = features[0].getProperties().features;
      if (clusterFeatures.length === 1) {
        selectedStop = clusterFeatures[0];
      }
    }
    setSelectedStop(selectedStop);
  };

  return (
    <MapContainer>
      <StationButtons>
        <div className="d-flex flex-row-reverse">
          <div className="card border-success d-flex flex-row p-1">
            Bus Stops
            <div className="form-switch ms-2">
              <input
                className="form-check-input bg-primary border-primary"
                type="checkbox"
                role="switch"
                id="showStops"
                checked={showStops}
                onChange={() => setShowStops(!showStops)}
              />
            </div>
          </div>
        </div>
      </StationButtons>

      <Map view={view}>
        <Layers>
          <Tile source={osm} />
          <Vector
            name="boundary"
            source={boundaryVectorSource}
            style={BoundaryStyle}
          />
          <Mask feature={fareZoneFeature} fill={MaskFill} />
          <Vector
            name="stops"
            source={stopClusterSource}
            style={ClusterStyle}
            visible={showStops}
          />
          <Vector
            name="stations"
            source={stationsVectorSource}
            style={StationStyle}
          />
        </Layers>
        <Controls>
          <ScaleBar />
          <FullScreen />
          <ZoomButtons />
          <ZoomSlide />
        </Controls>
        <Interactions>
          <ZoomToCluster />
          <MapClick onClick={stopClickHandler} layerNames={["stops"]} />
        </Interactions>
        <Overlays>
          <Overlays>
            <PopupOverlay
              coordinate={(
                selectedStop?.getGeometry() as olPoint | undefined
              )?.getCoordinates()}
              placement="top">
              <PopoverBody className="p-0" style={{ borderRadius: 7 }}>
                <div
                  className="bg-info text-white py-0 px-2 text-nowrap"
                  style={{ borderTopLeftRadius: 7, borderTopRightRadius: 7 }}>
                  <FontAwesomeIcon icon={faBus} />{" "}
                  {selectedStop?.getProperties().name}
                </div>
                <div className="text-info py-0 px-2 text-nowrap">
                  Stop Code:{" "}
                  {selectedStop?.getProperties().naptanCode ?? "unknown"}
                </div>
              </PopoverBody>
            </PopupOverlay>
          </Overlays>
        </Overlays>
      </Map>
    </MapContainer>
  );
}

export default Public;
